import type { SearchMarketCapCategory } from './facetOptions';
import { SEARCH_MARKET_CAP_CATEGORY_LABELS } from './facetOptions';
import {
  CountBubble,
  FacetApplyPopover,
  FacetCheckboxButton,
  useSyncedPendingFacetState,
} from './searchFacetFilterPrimitives';
import {
  ALL_MARKET_CAP_CATEGORIES,
  getMarketCapTriggerLabel,
  getToggledMarketCapValues,
} from './searchFacetFilterUtils';

function MarketCapTriggerContent({
  selectedMarketCapCategories,
}: {
  selectedMarketCapCategories: SearchMarketCapCategory[];
}) {
  const selectedCount = selectedMarketCapCategories.length;

  return (
    <>
      <span className="min-w-0 truncate">Market Cap</span>
      {selectedCount > 0 && selectedCount < ALL_MARKET_CAP_CATEGORIES.length ? (
        <CountBubble count={selectedCount} />
      ) : null}
    </>
  );
}

export function MarketCapFacetMenu({
  selectedMarketCapCategories,
  triggerClassName,
  popupClassName,
  popupMatchTrigger,
  onApply,
}: {
  selectedMarketCapCategories: SearchMarketCapCategory[];
  triggerClassName?: string;
  popupClassName?: string;
  popupMatchTrigger?: boolean;
  onApply: (marketCapCategories: SearchMarketCapCategory[] | undefined) => void;
}) {
  const selectedMarketCapCategoryKey = selectedMarketCapCategories.join('|');
  const [pendingMarketCapCategories, setPendingMarketCapCategories] =
    useSyncedPendingFacetState(
      selectedMarketCapCategories,
      selectedMarketCapCategoryKey,
    );
  const pendingMarketCapValues = new Set(pendingMarketCapCategories);
  const resetPendingMarketCapCategories = () => {
    setPendingMarketCapCategories(selectedMarketCapCategories);
  };

  return (
    <FacetApplyPopover
      triggerClassName={triggerClassName}
      triggerTrackProps={{
        'data-track': 'search_facet_open',
        'data-track-label': 'Market Cap',
        'data-track-context': 'search_facet',
      }}
      popupClassName={popupClassName}
      popupMatchTrigger={popupMatchTrigger}
      label={getMarketCapTriggerLabel(selectedMarketCapCategories)}
      triggerContent={
        <MarketCapTriggerContent
          selectedMarketCapCategories={selectedMarketCapCategories}
        />
      }
      hideSearch
      onClear={() => {
        setPendingMarketCapCategories([]);
      }}
      onClose={resetPendingMarketCapCategories}
      onApply={() => {
        onApply(
          pendingMarketCapCategories.length === 0 ||
            pendingMarketCapCategories.length ===
              ALL_MARKET_CAP_CATEGORIES.length
            ? undefined
            : pendingMarketCapCategories,
        );
      }}
      onSelectAll={() => {
        setPendingMarketCapCategories(ALL_MARKET_CAP_CATEGORIES);
      }}
      applyDisabled={pendingMarketCapCategories.length === 0}
    >
      {() =>
        ALL_MARKET_CAP_CATEGORIES.map((category) => (
          <FacetCheckboxButton
            key={category}
            label={SEARCH_MARKET_CAP_CATEGORY_LABELS[category]}
            checked={pendingMarketCapValues.has(category)}
            onClick={() => {
              setPendingMarketCapCategories((current) =>
                getToggledMarketCapValues(current, category),
              );
            }}
          />
        ))
      }
    </FacetApplyPopover>
  );
}
